/**
 * FrameworkDetailModal - Shows framework-specific compliance details
 */
import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, Spin, Statistic, Row, Col } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { getFrameworkDetails } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

function FrameworkDetailModal({ visible, onClose, scanId, frameworkName }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && scanId && frameworkName) {
      fetchDetails();
    }
  }, [visible, scanId, frameworkName]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await getFrameworkDetails(scanId, frameworkName);
      setDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch framework details:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'Control', dataIndex: 'control_code', key: 'control_code', width: 100 },
    { title: 'Name', dataIndex: 'control_name', key: 'control_name' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'fail' ? 'red' : status === 'pass' ? 'green' : 'orange'}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity) => (
        <Tag color={severity === 'critical' ? 'red' : severity === 'high' ? 'orange' : 'gold'}>
          {severity?.toUpperCase() || 'MEDIUM'}
        </Tag>
      )
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      key: 'confidence',
      width: 100,
      render: (conf) => `${conf}%`
    },
    { title: 'Evidence', dataIndex: 'evidence_count', key: 'evidence_count', width: 100 }
  ];

  return (
    <Modal
      title={`${frameworkName} Compliance Details`}
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : details ? (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Statistic
                title="Framework Score"
                value={details.score}
                suffix="/ 100"
                valueStyle={{ color: details.score >= 85 ? '#2e7d32' : details.score >= 70 ? '#ed6c02' : '#d32f2f' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Passing"
                value={details.passing}
                suffix={`/ ${details.total_controls}`}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#2e7d32' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Failing"
                value={details.failing}
                suffix={`/ ${details.total_controls}`}
                prefix={<CloseCircleOutlined />}
                valueStyle={{ color: '#d32f2f' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Warnings"
                value={details.warnings}
                suffix={`/ ${details.total_controls}`}
                prefix={<WarningOutlined />}
                valueStyle={{ color: '#ed6c02' }}
              />
            </Col>
          </Row>
          <Table
            dataSource={details.controls || []}
            columns={columns}
            rowKey="control_code"
            pagination={{ pageSize: 20 }}
            size="small"
          />
        </>
      ) : null}
    </Modal>
  );
}

export default FrameworkDetailModal;
